import React, {useContext, useEffect, useMemo, useState} from "react";
import {toast} from "react-toastify";
import {
    Avatar, Box, ButtonBase, IconButton, InputBase, MenuItem, Select, Skeleton, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Delete02Icon, Search01Icon} from "@hugeicons/core-free-icons";
import {UserRepository} from "../../repositories/UserRepository";
import {SignInContext} from "../../context/SignInContext";
import useConfirm from "../../hooks/useConfirm";
import {controlSx, Panel, PillSelect} from "../registry-project/dashboard/components/Panel";
import {getRoleColor} from "../../utils/roleColors";
import {getInitials} from "../../utils/userDisplay";

const DEFAULT_ROLES = ["admin", "manager", "user"];

const getRoles = (user) => user?.authorityList?.map(item => item?.authority) || user?.roles || [];

const headCellSx = {
    height: 40, py: 0.5, px: 1.5, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0",
    bgcolor: "#FAFAFD", borderBottom: "1px solid #ECECF3", whiteSpace: "nowrap",
};
const cellSx = {py: 1.25, px: 1.5, fontSize: 13, color: "#23264D", borderBottom: "1px solid #F2F2F7"};
const deleteIconButtonSx = {
    borderRadius: "50% !important", color: "#8A8AA0",
    "&:hover": {bgcolor: "rgba(220, 38, 38, 0.08)", color: "#DC2626"},
};
const roleSelectSx = {
    minWidth: 130, fontSize: 13, borderRadius: "10px",
    "& .MuiOutlinedInput-notchedOutline": {borderColor: "#E4E4EE"},
    "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: "#D6D6E4"},
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: "#8491F7"},
};

// Clickable status pill, replaces the plain on/off switch for enabling/disabling a user
const StatusPill = ({active, disabled, onClick}) => (
    <Tooltip title={disabled ? "" : (active ? "Click to deactivate" : "Click to activate")}>
        <span>
            <ButtonBase
                disabled={disabled}
                onClick={onClick}
                sx={{
                    height: 26, px: 1.1, gap: 0.65, borderRadius: "999px", fontSize: 12, fontWeight: 600,
                    color: active ? "#2F9A6A" : "#8A8AA0",
                    bgcolor: active ? "rgba(47, 154, 106, 0.12)" : "rgba(138, 138, 160, 0.12)",
                    "&:hover": {bgcolor: active ? "rgba(47, 154, 106, 0.2)" : "rgba(138, 138, 160, 0.2)"},
                    "&.Mui-disabled": {opacity: 0.6},
                }}
            >
                <Box sx={{width: 6, height: 6, borderRadius: "50%", bgcolor: active ? "#2F9A6A" : "#8A8AA0", flexShrink: 0}}/>
                {active ? "Active" : "Inactive"}
            </ButtonBase>
        </span>
    </Tooltip>
);

// Small color-key bar explaining what each role's avatar color means
const RoleLegend = ({roles}) => (
    <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} gap={2} sx={{
        px: 2.5, py: 1.25, borderBottom: "1px solid #ECECF3", bgcolor: "#FAFAFD",
    }}>
        <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0"}}>ROLE</Typography>
        {roles.map(role => (
            <Stack key={role} direction={"row"} alignItems={"center"} gap={0.75}>
                <Box sx={{width: 8, height: 8, borderRadius: "50%", bgcolor: getRoleColor(role), flexShrink: 0}}/>
                <Typography sx={{fontSize: 12, color: "#6B6B80", textTransform: "capitalize"}}>{role}</Typography>
            </Stack>
        ))}
    </Stack>
);

// Platform users: their contact info, role and active state.
export const RegistryUsersPage = () => {
    const {currentUser} = useContext(SignInContext);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [pagination, setPagination] = useState({page: 0, size: 10});

    const confirmDelete = useConfirm(
        "Are you sure you want to delete this user?",
        "This action cannot be undone. If the user still has related data, deletion will be blocked and you should deactivate the account instead."
    );

    const fetchUsers = () => {
        setLoading(true);
        UserRepository.getAll()
            .then(response => setUsers(response?.data || []))
            .catch(() => toast.error("Failed to get users"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleToggleStatus = (user) => {
        const enabled = !user?.enabled;
        UserRepository.updateStatus(user?.username, enabled)
            .then(() => {
                toast.success(`Successfully ${enabled ? "activated" : "deactivated"} user`);
                setUsers(prev => prev.map(u => u.username === user.username ? {...u, enabled} : u));
            })
            .catch(() => toast.error(`Failed to ${enabled ? "activate" : "deactivate"} user`));
    };

    // a user has a single role, so only the selected one is sent
    const handleChangeRole = (user, role) => {
        UserRepository.updateRoles(user?.username, [role])
            .then(() => {
                toast.success(`Changed role of "${user?.username}" to ${role}`);
                setUsers(prev => prev.map(u => u.username === user.username
                    ? {...u, roles: [role], authorityList: [{authority: role}]}
                    : u));
            })
            .catch(error => toast.error(error?.response?.data?.message || "Failed to change role"));
    };

    const handleDelete = async (user) => {
        const response = await confirmDelete({title: `Are you sure you want to delete "${user?.username}"?`});
        if (!response) return;

        UserRepository.delete(user?.username)
            .then(() => {
                toast.success("Successfully deleted user");
                setUsers(prev => prev.filter(u => u.username !== user.username));
            })
            .catch(error => {
                if (error?.response?.status === 409) {
                    toast.error(error?.response?.data?.message || "This user still has related data. Deactivate the account instead.");
                } else {
                    toast.error(error?.response?.data?.message || "Failed to delete user");
                }
            });
    };

    // keep roles that exist on users but are not in the default list selectable
    const roleOptions = useMemo(
        () => [...new Set([...DEFAULT_ROLES, ...users.flatMap(getRoles)])],
        [users]
    );

    const roleFilterOptions = useMemo(() => [
        {value: "", label: "All roles"},
        ...roleOptions.map(role => ({value: role, label: role.charAt(0).toUpperCase() + role.slice(1)})),
    ], [roleOptions]);

    const filteredUsers = useMemo(() => {
        const query = search.trim().toLowerCase();
        return users.filter(user => {
            if (roleFilter && !getRoles(user).includes(roleFilter)) return false;
            if (!query) return true;
            const fullName = `${user?.name || ""} ${user?.lastname || ""}`.toLowerCase();
            return fullName.includes(query)
                || user?.username?.toLowerCase()?.includes(query)
                || user?.email?.toLowerCase()?.includes(query)
                || user?.phoneNumber?.toLowerCase()?.includes(query);
        });
    }, [users, search, roleFilter]);

    const paginatedUsers = filteredUsers.slice(
        pagination.page * pagination.size,
        pagination.page * pagination.size + pagination.size
    );

    const emptyMessage = search ? `No users match "${search}"` : roleFilter ? `No users with role "${roleFilter}"` : "No users found";

    return (
        <Stack gap={3} sx={{p: {xs: 2, md: 3}, maxWidth: 1200, width: "100%", mx: "auto", boxSizing: "border-box"}}>
            <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
                <Box>
                    <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>ADMINISTRATION</Typography>
                    <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
                        Users
                    </Typography>
                    <Typography sx={{fontSize: 14, color: "#6B6B80"}}>
                        Manage platform users, their roles and access
                    </Typography>
                </Box>
                <Stack direction={"row"} alignItems={"center"} gap={1.25} flexWrap={"wrap"}>
                    <Box component={"label"} sx={{...controlSx, width: 240}}>
                        <HugeiconsIcon icon={Search01Icon} size={16}/>
                        <InputBase
                            placeholder={"Search users..."}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPagination(prev => ({...prev, page: 0}));
                            }}
                            inputProps={{"aria-label": "Search users"}}
                            sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                        />
                    </Box>
                    <PillSelect value={roleFilter} options={roleFilterOptions}
                                onChange={(value) => {
                                    setRoleFilter(value);
                                    setPagination(prev => ({...prev, page: 0}));
                                }}
                                placeholder={"Role"} ariaLabel={"Filter by role"}/>
                </Stack>
            </Stack>

            <Panel sx={{overflow: "hidden"}}>
                <RoleLegend roles={roleOptions}/>
                <TableContainer sx={{overflowX: "auto"}}>
                    <Table size={"small"} sx={{minWidth: 880}}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{...headCellSx, pl: 2.5}}>FULL NAME</TableCell>
                                <TableCell sx={headCellSx}>USERNAME</TableCell>
                                <TableCell sx={headCellSx}>EMAIL</TableCell>
                                <TableCell sx={headCellSx}>PHONE NUMBER</TableCell>
                                <TableCell sx={headCellSx}>ROLE</TableCell>
                                <TableCell sx={headCellSx}>ACTIVE</TableCell>
                                <TableCell sx={{...headCellSx, pr: 2.5}}/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && users.length === 0 && Array.from({length: 5}).map((_, index) => (
                                <TableRow key={index}>
                                    <TableCell colSpan={7} sx={{...cellSx, px: 2.5}}><Skeleton height={32}/></TableCell>
                                </TableRow>
                            ))}
                            {!loading && paginatedUsers?.map(user => {
                                const isSelf = user?.username === currentUser?.username;
                                const role = getRoles(user)[0] || "";
                                return (
                                    <TableRow key={user?.username} hover sx={{"&.MuiTableRow-hover:hover": {bgcolor: "#FAFAFD"}}}>
                                        <TableCell sx={{...cellSx, pl: 2.5, fontWeight: 600}}>
                                            <Stack direction={"row"} alignItems={"center"} gap={1.25}>
                                                <Avatar sx={{
                                                    width: 28, height: 28, fontSize: 13, fontWeight: 700,
                                                    bgcolor: `${getRoleColor(role)}1F`, color: getRoleColor(role),
                                                }}>
                                                    {getInitials(user)}
                                                </Avatar>
                                                <Box component={"span"}>
                                                    {`${user?.name || ""} ${user?.lastname || ""}`.trim() || "-"}
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={cellSx}>{user?.username}</TableCell>
                                        <TableCell sx={{...cellSx, color: "#4A4A63"}}>{user?.email || "-"}</TableCell>
                                        <TableCell sx={{...cellSx, color: "#4A4A63"}}>{user?.phoneNumber || "-"}</TableCell>
                                        <TableCell sx={cellSx}>
                                            <Tooltip title={isSelf ? "You cannot change your own role" : ""}>
                                                <span>
                                                    <Select
                                                        size={"small"}
                                                        value={role}
                                                        displayEmpty
                                                        disabled={isSelf}
                                                        onChange={(e) => handleChangeRole(user, e.target.value)}
                                                        sx={roleSelectSx}
                                                    >
                                                        {!role && <MenuItem value={""} disabled>No role</MenuItem>}
                                                        {roleOptions.map(option => (
                                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={cellSx}>
                                            <StatusPill active={!!user?.enabled} onClick={() => handleToggleStatus(user)}/>
                                        </TableCell>
                                        <TableCell padding={"checkbox"} sx={{...cellSx, pr: 2.5}}>
                                            <Tooltip title={isSelf ? "You cannot delete your own account" : "Delete user"}>
                                                <span>
                                                    <IconButton
                                                        size={"small"} disabled={isSelf} sx={deleteIconButtonSx}
                                                        onClick={() => handleDelete(user)}
                                                    >
                                                        <HugeiconsIcon icon={Delete02Icon} size={16}/>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {!loading && filteredUsers?.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{...cellSx, borderBottom: 0, py: 6, textAlign: "center", color: "#8A8AA0"}}>
                                        {emptyMessage}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component={"div"}
                    count={filteredUsers?.length || 0}
                    page={pagination.page}
                    onPageChange={(e, page) => setPagination(prev => ({...prev, page}))}
                    rowsPerPage={pagination.size}
                    onRowsPerPageChange={(e) => setPagination(prev => ({...prev, page: 0, size: +e.target.value}))}
                    rowsPerPageOptions={[10, 25, 50]}
                    sx={{borderTop: "1px solid #ECECF3", color: "#6B6B80"}}
                />
            </Panel>
        </Stack>
    );
};
